import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { z } from "zod";
import { ChevronLeft, ChevronRight, Download, Mail, RefreshCw, RotateCw, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { deleteEmailLogRow, exportEmailDashboard, getEmailDetail, listEmailDashboard, requeueDlqEmail } from "@/lib/email-log.functions";
import { EmailTrendChart } from "@/components/admin/EmailTrendChart";
import { TopTemplatesWidget } from "@/components/admin/TopTemplatesWidget";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const TIME_RANGES = [
  { hours: 24, label: "24 timer" },
  { hours: 24 * 7, label: "7 dage" },
  { hours: 24 * 30, label: "30 dage" },
  { hours: 24 * 90, label: "90 dage" },
] as const;

const STATUS_OPTIONS = [
  { value: "all", label: "Alle statusser" },
  { value: "sent", label: "Sendt" },
  { value: "failed", label: "Fejlet" },
  { value: "suppressed", label: "Undertrykt" },
  { value: "pending", label: "Afventer" },
] as const;

const searchSchema = z.object({
  hours: fallback(z.union([z.literal(24), z.literal(168), z.literal(720), z.literal(2160)]), 168).default(168),
  template: fallback(z.string(), "all").default("all"),
  status: fallback(z.enum(["all", "sent", "failed", "suppressed", "pending"]), "all").default("all"),
  q: fallback(z.string(), "").default(""),
  page: fallback(z.number().int().min(0), 0).default(0),
});

type EmailSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/admin/emails")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Email-log – AutoXChange Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EmailDashboard,
});

function EmailDashboard() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/emails" });
  const fetchDashboard = useServerFn(listEmailDashboard);
  const exportDashboard = useServerFn(exportEmailDashboard);
  const requeue = useServerFn(requeueDlqEmail);
  const deleteRow = useServerFn(deleteEmailLogRow);
  const queryClient = useQueryClient();
  const [isExporting, setIsExporting] = useState(false);
  // Selected row for the detail modal — { messageId, rowId } pair so we can
  // look up by message_id when present, falling back to row id otherwise.
  const [selected, setSelected] = useState<{ messageId: string | null; rowId: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ messageId: string | null; rowId: string; recipient: string } | null>(null);
  const fetchDetail = useServerFn(getEmailDetail);

  const setSearch = (next: Partial<EmailSearch>) => {
    navigate({
      search: (prev: EmailSearch) => ({ ...prev, ...next, ...(next.page === undefined ? { page: 0 } : {}) }),
      replace: true,
    });
  };

  // Local input state for the recipient search box, debounced into URL search.
  const [searchInput, setSearchInput] = useState(search.q);
  // Sync local input when URL changes externally (back/forward, clear button).
  useEffect(() => {
    setSearchInput(search.q);
  }, [search.q]);
  // Debounce typing → URL update (which triggers a refetch via queryKey).
  useEffect(() => {
    if (searchInput === search.q) return;
    const t = setTimeout(() => setSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["admin-emails", search.hours, search.template, search.status, search.q, search.page],
    queryFn: () =>
      fetchDashboard({
        data: {
          sinceHours: search.hours,
          template: search.template === "all" ? null : search.template,
          status: search.status === "all" ? null : search.status,
          recipientSearch: search.q.trim() === "" ? null : search.q.trim(),
          page: search.page,
          pageSize: 50,
        },
      }),
    // Auto-refresh every 60s like the main admin dashboard.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  const lastUpdatedLabel = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" })
    : null;

  const requeueMutation = useMutation({
    mutationFn: (messageId: string) => requeue({ data: { messageId, queue: "transactional_emails" } }),
    onSuccess: () => {
      toast.success("Email er gen-køet", {
        description: "Dispatcheren forsøger at sende den igen ved næste kørsel (op til 1 min).",
      });
      queryClient.invalidateQueries({ queryKey: ["admin-emails"] });
    },
    onError: (err: unknown) => {
      toast.error("Kunne ikke gen-sende emailen", {
        description: err instanceof Error ? err.message : "Ukendt fejl",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (input: { messageId: string | null; rowId: string }) =>
      deleteRow({ data: { messageId: input.messageId, rowId: input.messageId ? null : input.rowId } }),
    onSuccess: ({ deleted }) => {
      toast.success(
        deleted === 1 ? "Emaillinje slettet" : `${deleted} emaillinjer slettet`,
        { description: "Loggen er opdateret." },
      );
      queryClient.invalidateQueries({ queryKey: ["admin-emails"] });
      queryClient.invalidateQueries({ queryKey: ["admin-dlq-count"] });
      setConfirmDelete(null);
      setSelected(null);
    },
    onError: (err: unknown) => {
      toast.error("Kunne ikke slette rækken", {
        description: err instanceof Error ? err.message : "Ukendt fejl",
      });
    },
  });

  const detailQuery = useQuery({
    queryKey: ["admin-emails-detail", selected?.messageId, selected?.rowId],
    queryFn: () =>
      fetchDetail({
        data: { messageId: selected!.messageId, rowId: selected!.rowId },
      }),
    enabled: !!selected,
    staleTime: 30_000,
  });

  const stats = data?.stats ?? { total: 0, sent: 0, failed: 0, suppressed: 0, pending: 0, other: 0 };
  const templates = data?.templates ?? [];
  const sends = data?.sends ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.totalFiltered / data.pageSize)) : 1;

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const { rows } = await exportDashboard({
        data: {
          sinceHours: search.hours,
          template: search.template === "all" ? null : search.template,
          status: search.status === "all" ? null : search.status,
          recipientSearch: search.q.trim() === "" ? null : search.q.trim(),
        },
      });

      if (rows.length === 0) {
        toast.info("Ingen rækker at eksportere", { description: "De aktive filtre returnerede 0 emails." });
        return;
      }

      // RFC 4180 — quote every field, escape inner quotes by doubling them.
      const escape = (v: string | null | undefined) => `"${(v ?? "").replace(/"/g, '""')}"`;
      const header = ["Tid", "Skabelon", "Modtager", "Status", "Fejl", "Message ID"];
      const lines = [header.join(",")];
      for (const r of rows) {
        lines.push(
          [
            escape(new Date(r.created_at).toISOString()),
            escape(r.template_name),
            escape(r.recipient_email),
            escape(r.status),
            escape(r.error_message),
            escape(r.message_id),
          ].join(","),
        );
      }
      // BOM so Excel auto-detects UTF-8 (æ/ø/å survive).
      const csv = "\ufeff" + lines.join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `email-log-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Eksporterede ${rows.length} emails`);
    } catch (err) {
      toast.error("Kunne ikke eksportere", {
        description: err instanceof Error ? err.message : "Ukendt fejl",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const statCards = [
    { label: "Unikke emails", value: stats.total, color: "text-foreground" },
    { label: "Sendt", value: stats.sent, color: "text-green-600" },
    { label: "Fejlet", value: stats.failed, color: "text-destructive" },
    { label: "Undertrykt", value: stats.suppressed, color: "text-orange-600" },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Email-log</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Dedupliceret oversigt over email-sendinger med status og fejl
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdatedLabel && (
            <span className="text-xs text-muted-foreground" title="Auto-opdateres hvert 60. sekund">
              Sidst opdateret {lastUpdatedLabel}
            </span>
          )}
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={isExporting || isLoading}
            title="Download de filtrerede sendinger som CSV (åbnes i Excel/Numbers)"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
          >
            <Download className={`h-3.5 w-3.5 ${isExporting ? "animate-pulse" : ""}`} />
            {isExporting ? "Eksporterer…" : "Eksportér CSV"}
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Opdater
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((s) => (
          <div key={s.label} className="rounded-xl bg-card p-5 shadow-sm ring-1 ring-border">
            <p className="text-sm font-medium text-muted-foreground">{s.label}</p>
            <p className={`mt-2 text-3xl font-bold ${s.color}`}>{isLoading ? "–" : s.value}</p>
          </div>
        ))}
      </div>

      {/* Trend chart — sent / failed / suppressed over time, follows the active hour-window */}
      <div className="mt-6">
        <EmailTrendChart sinceHours={search.hours} />
      </div>

      {/* Top templates: 5 mest brugte + 5 oftest fejlende, samme tidsvindue */}
      <div className="mt-6">
        <TopTemplatesWidget sinceHours={search.hours} />
      </div>

      {/* Filters */}
      <div className="mt-6 rounded-xl bg-card p-5 shadow-sm ring-1 ring-border">
        <div className="mb-4">
          <label htmlFor="recipient-search" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Søg modtager-email
          </label>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="recipient-search"
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="fx kunde@eksempel.dk"
              className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-9 text-sm text-foreground focus:border-primary focus:outline-none"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                aria-label="Ryd søgning"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tidsrum</label>
            <div className="mt-2 flex flex-wrap gap-1 rounded-lg bg-muted p-0.5">
              {TIME_RANGES.map((r) => (
                <button
                  key={r.hours}
                  type="button"
                  onClick={() => setSearch({ hours: r.hours as EmailSearch["hours"], page: 0 })}
                  className={`flex-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition ${
                    search.hours === r.hours
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="template-filter"
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              Skabelon
            </label>
            <select
              id="template-filter"
              value={search.template}
              onChange={(e) => setSearch({ template: e.target.value, page: 0 })}
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              <option value="all">Alle skabeloner</option>
              {templates.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="status-filter"
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              Status
            </label>
            <select
              id="status-filter"
              value={search.status}
              onChange={(e) => setSearch({ status: e.target.value as EmailSearch["status"], page: 0 })}
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 rounded-xl bg-card shadow-sm ring-1 ring-border">
        <div className="border-b border-border p-4">
          <p className="text-sm text-muted-foreground">
            {isLoading ? "Indlæser…" : `Viser ${sends.length} af ${data?.totalFiltered ?? 0} unikke sendinger`}
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : sends.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Ingen email-sendinger matcher de valgte filtre
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Tid</th>
                  <th className="px-4 py-3 font-medium">Skabelon</th>
                  <th className="px-4 py-3 font-medium">Modtager</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Fejl</th>
                  <th className="px-4 py-3 text-right font-medium">Handling</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sends.map((row) => {
                  const status = row.status;
                  const isFailed =
                    status === "dlq" || status === "failed" || status === "bounced";
                  const badgeClass =
                    status === "sent"
                      ? "bg-green-500/15 text-green-600"
                      : isFailed
                        ? "bg-destructive/15 text-destructive"
                        : status === "suppressed" || status === "complained"
                          ? "bg-orange-500/15 text-orange-600"
                          : "bg-muted text-muted-foreground";
                  // Only DLQ rows can be re-enqueued — failed/bounced rows came from
                  // the send endpoint before the message ever reached the queue.
                  const canRequeue = status === "dlq" && !!row.message_id;
                  const isPending =
                    requeueMutation.isPending && requeueMutation.variables === row.message_id;
                  return (
                    <tr
                      key={row.id}
                      onClick={() => setSelected({ messageId: row.message_id, rowId: row.id })}
                      className="cursor-pointer text-card-foreground transition hover:bg-muted/50"
                      title="Klik for at se detaljer og statushistorik"
                    >
                      <td
                        className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground"
                        title={new Date(row.created_at).toLocaleString("da-DK")}
                      >
                        {new Date(row.created_at).toLocaleString("da-DK", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-4 py-2 text-xs font-medium">{row.template_name}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{row.recipient_email}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass}`}
                        >
                          {status}
                        </span>
                      </td>
                      <td
                        className="max-w-[280px] truncate px-4 py-2 text-xs text-muted-foreground"
                        title={row.error_message ?? undefined}
                      >
                        {row.error_message ?? "–"}
                      </td>
                      <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          {canRequeue ? (
                            <button
                              type="button"
                              onClick={() => row.message_id && requeueMutation.mutate(row.message_id)}
                              disabled={isPending}
                              title="Send denne email igen via køen"
                              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
                            >
                              <RotateCw className={`h-3 w-3 ${isPending ? "animate-spin" : ""}`} />
                              {isPending ? "Sender…" : "Send igen"}
                            </button>
                          ) : isFailed ? (
                            <span
                              className="text-[10px] text-muted-foreground"
                              title="Fejlen skete før beskeden nåede køen — kan ikke gen-sendes automatisk"
                            >
                              ikke i kø
                            </span>
                          ) : null}
                          <button
                            type="button"
                            onClick={() =>
                              setConfirmDelete({
                                messageId: row.message_id,
                                rowId: row.id,
                                recipient: row.recipient_email,
                              })
                            }
                            title={
                              row.message_id
                                ? "Slet alle log-rækker for denne email"
                                : "Slet denne log-række"
                            }
                            className="inline-flex items-center justify-center rounded-md border border-border bg-background p-1.5 text-muted-foreground transition hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
                            aria-label="Slet"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data && data.totalFiltered > data.pageSize && (
          <div className="flex items-center justify-between border-t border-border p-4 text-xs text-muted-foreground">
            <span>
              Side {search.page + 1} af {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={search.page === 0}
                onClick={() => setSearch({ page: search.page - 1 })}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 font-medium text-foreground transition hover:bg-muted disabled:opacity-40"
              >
                <ChevronLeft className="h-3 w-3" />
                Forrige
              </button>
              <button
                type="button"
                disabled={search.page + 1 >= totalPages}
                onClick={() => setSearch({ page: search.page + 1 })}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 font-medium text-foreground transition hover:bg-muted disabled:opacity-40"
              >
                Næste
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      <EmailDetailDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        isLoading={detailQuery.isLoading}
        error={detailQuery.error}
        rows={detailQuery.data?.rows ?? []}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slet email-logrækken?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.messageId
                ? `Alle log-rækker for emailen til ${confirmDelete.recipient} bliver slettet permanent fra loggen. Selve emailen er ikke berørt — kun historikken.`
                : `Denne log-række (${confirmDelete?.recipient}) bliver slettet permanent. Selve emailen er ikke berørt — kun historikken.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Annuller</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!confirmDelete) return;
                deleteMutation.mutate({
                  messageId: confirmDelete.messageId,
                  rowId: confirmDelete.rowId,
                });
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Sletter…" : "Slet"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type DetailRow = {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  metadata: unknown;
  created_at: string;
};

function EmailDetailDialog({
  open,
  onClose,
  isLoading,
  error,
  rows,
}: {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  error: unknown;
  rows: DetailRow[];
}) {
  // Latest row drives the header summary; timeline shows all rows oldest-first.
  const latest = rows.length > 0 ? rows[rows.length - 1] : null;
  const first = rows.length > 0 ? rows[0] : null;
  // Prefer the most recent non-empty metadata payload.
  const payload = [...rows]
    .reverse()
    .find((r) => r.metadata && typeof r.metadata === "object" && Object.keys(r.metadata as object).length > 0)?.metadata;

  const statusBadge = (s: string) => {
    if (s === "sent") return "bg-green-500/15 text-green-600";
    if (s === "dlq" || s === "failed" || s === "bounced") return "bg-destructive/15 text-destructive";
    if (s === "suppressed" || s === "complained") return "bg-orange-500/15 text-orange-600";
    return "bg-muted text-muted-foreground";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Email-detaljer</DialogTitle>
          <DialogDescription>
            Fuld statushistorik og metadata for denne email-sending
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2 py-4">
            <div className="h-6 animate-pulse rounded bg-muted" />
            <div className="h-20 animate-pulse rounded bg-muted" />
            <div className="h-32 animate-pulse rounded bg-muted" />
          </div>
        ) : error ? (
          <p className="py-6 text-sm text-destructive">
            {error instanceof Error ? error.message : "Kunne ikke hente detaljer"}
          </p>
        ) : !latest ? (
          <p className="py-6 text-sm text-muted-foreground">Ingen data</p>
        ) : (
          <div className="space-y-5">
            {/* Summary */}
            <div className="grid gap-3 rounded-lg border border-border bg-muted/30 p-4 text-sm sm:grid-cols-2">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Modtager
                </p>
                <p className="mt-1 break-all text-foreground">{latest.recipient_email}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Skabelon
                </p>
                <p className="mt-1 text-foreground">{latest.template_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Seneste status
                </p>
                <p className="mt-1">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadge(latest.status)}`}>
                    {latest.status}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Message ID
                </p>
                <p className="mt-1 break-all font-mono text-xs text-foreground">
                  {latest.message_id ?? <span className="text-muted-foreground">(ingen)</span>}
                </p>
              </div>
              {first && (
                <div className="sm:col-span-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Først logget
                  </p>
                  <p className="mt-1 text-foreground">
                    {new Date(first.created_at).toLocaleString("da-DK")}
                  </p>
                </div>
              )}
            </div>

            {/* Timeline */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-foreground">
                Statushistorik ({rows.length} {rows.length === 1 ? "begivenhed" : "begivenheder"})
              </h3>
              <ol className="relative space-y-3 border-l-2 border-border pl-5">
                {rows.map((r) => (
                  <li key={r.id} className="relative">
                    <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-background bg-primary" />
                    <div className="rounded-md border border-border bg-card p-3 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadge(r.status)}`}>
                          {r.status}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(r.created_at).toLocaleString("da-DK")}
                        </span>
                      </div>
                      {r.error_message && (
                        <p className="mt-2 whitespace-pre-wrap break-words text-xs text-destructive">
                          {r.error_message}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Payload */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Metadata / payload</h3>
              {payload ? (
                <pre className="max-h-72 overflow-auto rounded-md bg-muted p-3 text-xs text-foreground">
                  {JSON.stringify(payload, null, 2)}
                </pre>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Ingen metadata gemt for denne email
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
